import React, { useRef, useEffect } from 'react';
import { Message } from 'types';
import MessageListItem from './MessageListItem';
import { LoadingSpinner } from 'components/ui/LoadingSpinner';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  isStreaming = false,
  streamingContent = '',
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]); // Also scroll when streaming content updates

  return (
    <div className="space-y-4 mb-0">
      {messages.map((message) => (
        <MessageListItem key={message.id} message={message} />
      ))}

      {/* Show streaming message with live content */}
      {isStreaming && streamingContent && (
        <div className="flex justify-start">
          <div className="max-w-3xl rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-900">
            <div className="prose prose-sm max-w-none text-sm">
              {streamingContent}
              <span className="inline-block w-2 h-4 ml-1 bg-purple-600 animate-pulse" />
            </div>
          </div>
        </div>
      )}

      {/* Show "Thinking..." when loading or streaming but no content yet */}
      {(isLoading || (isStreaming && !streamingContent)) && (
        <div className="flex justify-start">
          <div className="max-w-3xl rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-900">
            <div className="flex items-center gap-2">
              <LoadingSpinner size="sm" />
              <p className="text-gray-500 text-sm">Thinking...</p>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
